import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Button from '../../components/Button';
import Form, { Field } from '../../components/Form';
import { gql } from '../../graphql/generated/gql';
import AccountContentContainer from '../AccountContentContainer';
import * as styles from './ForgotView.module.scss';

const REQUEST_PASSWORD_TOKEN = gql(/** @complexity 2 */ `
  mutation requestPasswordToken($email: String!, $type: String!) {
    requestPasswordToken(email: $email, type: $type)
  }
`);

type ForgotViewValues = {
  email: string;
  type: string;
};

const ForgotView = () => {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { effectiveHost } = window.__slab.PUBLIC_ORG;

  const [requestToken] = useMutation(REQUEST_PASSWORD_TOKEN);

  const onSubmit = (values: ForgotViewValues) =>
    requestToken({ variables: values }).then(() => {
      const entity =
        values.type === 'magic' ? 'magic log in' : 'reset password';

      setSuccessMessage(
        `A ${entity} link was sent to ${values.email}. Please check your email.`,
      );
    });

  return (
    <AccountContentContainer title="Forgot password" subtitle={effectiveHost}>
      {successMessage ? (
        <div className={styles.successMessage}>{successMessage}</div>
      ) : (
        <Form<ForgotViewValues>
          initialValues={{ email: '', type: '' }}
          onSubmit={onSubmit}
          apiErrorMessages={{
            NOT_FOUND: "Sorry, we can't find the email for this team",
            UNVERIFIED_ORGANIZATION:
              'You need to verify your email before logging in. Please contact support if you are having problems.',
          }}
        >
          {({ isSubmitting, setFieldValue }) => (
            <>
              <Field
                id="email"
                name="email"
                type="email"
                placeholder="Your email"
                required
              />
              <Button
                className={styles.resetLinkButton}
                disabled={isSubmitting}
                variant="primary"
                block
                type="submit"
                onClick={() => {
                  setFieldValue('type', 'reset');
                }}
              >
                Send reset link
              </Button>

              <AccountContentContainer.Divider />

              <Button
                disabled={isSubmitting}
                variant="primary"
                block
                type="submit"
                onClick={() => {
                  setFieldValue('type', 'magic');
                }}
              >
                Send magic link
              </Button>
              <div className={styles.emailDescription}>
                <div className="description">
                  We will email you a link so you can log in without typing
                  anything.
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </AccountContentContainer>
  );
};

export default ForgotView;
